import React, { useState } from "react";
import {
  Container,
  Title,
  Text,
  SimpleGrid,
  Image,
  Box,
  UnstyledButton,
  Overlay,
  Transition,
  rem,
  Stack,
  Badge,
} from "@mantine/core";
import { IconMaximize } from "@tabler/icons-react";
import { useTranslation } from "../context/TranslationContext";
import ImageLightbox from "../components/ImageLightbox";

export default function Gallery() {
  const { t } = useTranslation();
  const [opened, setOpened] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Gallery images setup
  const totalImages = 10;
  const galleryImages = Array.from({ length: totalImages }, (_, i) => ({
    id: i + 1,
    src: `/images/gallery/${i + 1}.jpeg`,
    alt: `Yogi Foods Gallery ${i + 1}`,
  }));

  const openImage = (index) => {
    setSelectedIndex(index);
    setOpened(true);
  };

  return (
    <Box pb={rem(100)}>
      {/* 1. HEADER SECTION - Matching About Page Style */}
      <Container size="lg" pt={rem(80)} pb={rem(60)}>
        <Stack gap="xs" align="center">
          <Badge variant="dot" color="blue" size="lg">
            {t("galleryBadge", "Visuals")}
          </Badge>
          <Title
            order={1}
            size={rem(48)}
            ta="center"
            fw={900}
            style={{ letterSpacing: rem(-1) }}
          >
            {t("galleryTitle", "Our Gallery")}
          </Title>
          <Text c="dimmed" size="lg" ta="center" maw={600}>
            {t(
              "galleryTagline",
              "A visual journey through the world of Yogi Foods"
            )}
          </Text>
        </Stack>
      </Container>

      {/* 2. MASONRY-STYLE GRID */}
      <Container size="lg">
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="lg">
          {galleryImages.map((image, index) => (
            <GalleryItem
              key={image.id}
              image={image}
              onClick={() => openImage(index)}
              buttonLabel={t("openImage", `Open image ${image.id} in lightbox`)}
            />
          ))}
        </SimpleGrid>
      </Container>

      {/* 3. LIGHTBOX MODAL */}
      <ImageLightbox
        opened={opened}
        onClose={() => setOpened(false)}
        images={galleryImages.map((img) => ({ src: img.src, alt: img.alt }))}
        selectedIndex={selectedIndex}
        onSelectIndex={setSelectedIndex}
      />
    </Box>
  );
}

/* Internal Sub-component for individual items */
function GalleryItem({ image, onClick, buttonLabel }) {
  const [hovered, setHovered] = useState(false);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <UnstyledButton
      onClick={onClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      aria-label={buttonLabel}
      style={{
        position: "relative",
        overflow: "hidden",
        borderRadius: rem(12),
        padding: 0,
        display: "block",
        width: "100%",
        cursor: "pointer",
      }}
    >
      <Image
        src={image.src}
        alt={image.alt}
        height={300}
        style={{
          transition: "transform 0.4s ease",
          transform: hovered ? "scale(1.05)" : "scale(1)",
        }}
      />

      <Transition mounted={hovered} transition="fade" duration={300}>
        {(styles) => (
          <Box style={styles}>
            <Overlay color="#000" opacity={0.3} zIndex={1} />
            <Box
              pos="absolute"
              style={{
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 2,
              }}
            >
              <IconMaximize color="white" size={32} stroke={1.5} aria-hidden="true" />
            </Box>
          </Box>
        )}
      </Transition>
    </UnstyledButton>
  );
}
